import { useEffect } from "react";

import { EditorContent } from "@tiptap/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { chatExtensions } from "@/components/tiptap/extensions";
import { chatScope } from "@/components/tiptap/utils/uploadScope";
import { useEditorCommon } from "@/components/tiptap/utils/useEditorCommon";
import { useEditorStateCommon } from "@/components/tiptap/utils/useEditorStateCommon";
import { cursorIsActive } from "@/models/CursorModel";
import { useStore } from "@/store/RootStoreProvider";
import { useChats } from "@/store/useChats";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { ContentTabbable, contentTabbableKey } from "./ContentTabbable";

import type { ChatMessageModel } from "@/models/chat/ChatMessageModel";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { DocumentModel } from "@/models/DocumentModel";

type ChatTiptapRenderPlace = "chat-input" | "chat-message";

export const ChatTiptap = observer(function ChatTiptap({
  doc,
  className,
  editorClassName,
  editorStyle = "",
  editable = false,
  label,
  chat,
  message,
  renderPlace,
  autoFocus,
  rootRef,
  onEscape,
  placeholder,
  contentTabbable = true,
}: {
  doc: DocumentModel;
  className?: string;
  editorClassName?: string;
  editorStyle?: string;
  editable?: boolean;
  label?: string;
  chat?: ChatModel;
  message?: ChatMessageModel;
  renderPlace: ChatTiptapRenderPlace;
  autoFocus?: boolean;
  rootRef?: React.Ref<HTMLDivElement>;
  onEscape?: () => void;
  placeholder?: string;
  /** For a message: whether its links and buttons are in the Tab order */
  contentTabbable?: boolean;
}) {
  const t = useTranslations();
  const chatsStore = useChats();
  const { userSettings } = useStore();
  const uploadScope = chat && chatScope(chat.uid);

  const editor = useEditorCommon({
    doc,
    chat,
    message,
    editable,
    autoFocus,
    uploadScope,
    immediatelyRender: renderPlace === "chat-input" ? false : undefined,
    mathIndentation: false,
    extensions: [
      ...chatExtensions({
        doc,
        renderPlace,
        uploadScope,
        placeholder: (node, docNode) => {
          if (
            node.type.name === "paragraph" &&
            renderPlace === "chat-input" &&
            docNode.content.size <= 2
          ) {
            if (placeholder !== undefined) {
              return placeholder;
            }

            return chat?.withBot
              ? t("chat.chatWithBotPlaceholder")
              : t("chat.chatWithTeamPlaceholder");
          }

          return undefined;
        },
      }),
      // Messages start out of the Tab order; the message list turns a message's links back on
      ...(renderPlace === "chat-message"
        ? [ContentTabbable.configure({ tabbable: contentTabbable })]
        : []),
    ],
    editorProps: {
      attributes: editable
        ? {
            enterKeyHint: "enter",
            role: "textbox",
            "aria-multiline": "true",
            "aria-label": label ?? "",
            class: twMerge(
              "outline-none font-sans text-[15px] leading-[1.3] tracking-tight",
              editorClassName,
            ),
            style: editorStyle,
          }
        : {
            class: twMerge(
              "outline-none font-sans text-[15px] leading-[1.6] tracking-tight",
              editorClassName,
            ),
            style: editorStyle,
          },
      handleKeyDown: (_view, event) => {
        if (event.key !== "Escape" || renderPlace !== "chat-input" || !chat) {
          return false;
        }
        if (event.defaultPrevented || cursorIsActive(doc.cursor)) {
          return false;
        }
        trackEvent(chat.withBot ? "chat_with_bot_closed" : "chat_with_team_closed", {
          source: "esc_key",
        });
        onEscape?.();
        chatsStore.closeChat();
        return true;
      },
      handlePaste: (_view, event) => {
        if (!chat) return false;

        const files = Array.from(event.clipboardData?.files ?? []);
        if (files.length === 0) return false;

        chat.uploadFiles(files).catch((err) => {
          console.error("Failed to upload chat files:", err);
        });

        return true;
      },
    },
  });

  const editorState = useEditorStateCommon({ editor });

  useEffect(() => {
    if (editor && editorState) {
      runInAction(() => {
        doc.editor.tiptap = editor;
        doc.editor.editorState = editorState;
      });
    }
  }, [editor, editorState, doc]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || renderPlace !== "chat-message") return;
    editor.view.dispatch(editor.state.tr.setMeta(contentTabbableKey, contentTabbable));
  }, [editor, renderPlace, contentTabbable]);

  return (
    <div ref={rootRef} className={twMerge("flex flex-col h-full", className)}>
      <EditorContent
        editor={editor}
        className={twMerge(
          "outline-none",
          "w-full h-full",
          userSettings.codeTheme === "light" &&
            "[&_*_code]:dark:text-black [&_*_code]:dark:bg-gray-100",
          userSettings.codeTheme === "dark" && "[&_*_code]:text-white [&_*_code]:bg-gray-900",
          editable && "cursor-text",
          renderPlace === "chat-message" &&
            "[&_:is(h2,h3,h4)]:mt-7.5 [&_:is(h2,h3,h4)]:mb-2.5 [&_.tiptap>*:first-child]:mt-0 [&_.tiptap>*:first-child>[data-node-view-wrapper]]:mt-0 [&_.tiptap>*:last-child]:mb-0 [&_.tiptap>*:last-child>[data-node-view-wrapper]]:mb-0",
        )}
      />
    </div>
  );
});
